'use client';

import { useMergeRefs } from '@floating-ui/react';
import { IconCalendarFilled } from '@zillow/constellation-icons/react/filled/icon-calendar-filled';
import { format } from 'date-fns';
import { type ComponentProps, forwardRef, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { Slot } from '../slot';
import { DatePickerRootContext } from './context/date-picker-root.context';
import { datePickerTriggerRecipe } from './styles/date-picker-trigger.styles';
import type { DatePickerTriggerPropsInterface } from './types/date-picker-trigger.types';

/**
 * @since 10.0.0
 */
export const DatePickerTrigger = forwardRef<HTMLButtonElement, DatePickerTriggerPropsInterface>(
  (props: DatePickerTriggerPropsInterface, forwardedRef) => {
    const { datePicker, size, disabled, error, readOnly } = useContext(DatePickerRootContext);

    const title = useMemo(() => {
      if (!datePicker.value) {
        return 'Choose date';
      }

      try {
        return `Change date, ${format(datePicker.value, 'EEEE, MMMM d, yyyy')}`;
      } catch {
        return 'Choose date';
      }
    }, [datePicker]);

    const { children, className, css: cssProp, ...rest } = props;

    const mergedRefs = useMergeRefs([datePicker.refs.setReference, forwardedRef]);

    const commonProps = useMemo(() => {
      const mergedReferenceProps = datePicker.getReferenceProps(rest) as ComponentProps<'button'>;
      const { 'aria-haspopup': _ariaHasPopup, ...referenceProps } = mergedReferenceProps;

      /*
       * Floating UI's `useRole` only sets `aria-controls` while open. Always pin it to the
       * stable contentId so closed triggers still advertise the controlled panel.
       */
      return {
        'ref': mergedRefs,
        'data-c11n-version': __C11N_VERSION__,
        'data-c11n-component': 'DatePicker.Trigger',
        ...referenceProps,
        'aria-controls': datePicker.contentId,
        'className': cx('c11n_date-picker__trigger', className),
      };
    }, [mergedRefs, datePicker, rest, className]);

    // Early return for custom children
    if (children) {
      const styles = css(datePickerTriggerRecipe.raw({ size, disabled, error, readOnly }), cssProp);

      return (
        <Slot
          {...commonProps}
          className={cx('c11n', 'c11n_date-picker__trigger', styles, className)}
        >
          {children}
        </Slot>
      );
    }

    // Default IconButton
    return (
      <IconButton
        {...commonProps}
        size={size}
        icon={
          <Icon>
            <IconCalendarFilled />
          </Icon>
        }
        title={title}
        shape="square"
        tone="brand"
        emphasis="bare"
        disabled={disabled}
        css={{
          ...datePickerTriggerRecipe.raw({ size, disabled, error, readOnly }),
          ...cssProp,
        }}
      />
    );
  },
);

DatePickerTrigger.displayName = 'DatePicker.Trigger';
